import { useEffect, useState } from "react";
import { Phone, Menu, X, CalendarCheck, Languages, Images } from "lucide-react";
import { scrollToId } from "./Reveal";
import { useLang } from "@/i18n";

export default function Navbar() {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const LINKS = [
        { id: "services", label: t.nav.services },
        { id: "doctors", label: t.nav.doctors },
        { id: "gallery", label: t.nav.gallery },
        { id: "appointment", label: t.nav.appointment },
        { id: "location", label: t.nav.location },
    ];

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    const toggleLang = () => setLang(lang === "en" ? "mr" : "en");

    return (
        <header
            data-testid="main-navigation-bar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#FAFAFA]/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_10px_40px_-24px_rgba(13,30,54,0.35)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }))}
                    className="flex items-center gap-3"
                    aria-label="Signita Wellness — back to top"
                >
                    <img src="/signita-logo.png" alt="Signita Wellness logo" className="h-10 w-auto object-contain md:h-11" />
                </button>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="group relative text-[13px] font-medium tracking-wide text-brand-navy/80 transition-colors duration-200 hover:text-brand-navy"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-lime transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>

                <div className="hidden items-center gap-5 lg:flex">
                    <button
                        data-testid="lang-toggle"
                        onClick={toggleLang}
                        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-2 text-xs font-bold text-brand-navy transition-all duration-300 hover:border-brand-olive hover:text-brand-olive"
                    >
                        <Languages className="h-3.5 w-3.5 text-brand-lime" />
                        {t.nav.toggle}
                    </button>
                    <a
                        data-testid="nav-phone-link"
                        href="tel:+917208991091"
                        className="flex items-center gap-2 font-mono text-[13px] text-brand-navy/80 transition-colors duration-200 hover:text-brand-olive"
                    >
                        <Phone className="h-4 w-4 text-brand-lime" />
                        7208991091
                    </a>
                    <button
                        data-testid="nav-book-appointment-btn"
                        onClick={() => go("appointment")}
                        className="group flex items-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-[13px] font-semibold text-white transition-all duration-300 hover:bg-brand-olive hover:shadow-[0_14px_30px_-12px_rgba(85,107,47,0.6)]"
                    >
                        <CalendarCheck className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-6" />
                        {t.nav.book}
                    </button>
                </div>

                <div className="flex items-center gap-2.5 lg:hidden">
                    <button
                        data-testid="lang-toggle-mobile"
                        onClick={toggleLang}
                        className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-2 text-[11px] font-bold text-brand-navy"
                    >
                        <Languages className="h-3.5 w-3.5 text-brand-lime" />
                        {t.nav.toggle}
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        className="rounded-full border border-slate-200 bg-white/80 p-2.5"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <div className={`overflow-hidden transition-all duration-500 lg:hidden ${open ? "max-h-[28rem] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className="mx-4 mb-4 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-card backdrop-blur-xl">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-mobile-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium text-brand-navy transition-colors duration-200 hover:bg-brand-olivelight"
                        >
                            {l.id === "gallery" && <Images className="h-4 w-4 text-brand-lime" />}
                            {l.label}
                        </button>
                    ))}
                    <a
                        data-testid="nav-mobile-phone-link"
                        href="tel:+917208991091"
                        className="mt-1 flex items-center gap-2 rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white"
                    >
                        <Phone className="h-4 w-4 text-brand-lime" />
                        7208991091
                    </a>
                </div>
            </div>
        </header>
    );
}
